// Defaults & Workout rebuild — every case action modal, mounted ONCE at the
// workspace level. The pre-rebuild freeze came from these forms living
// inside collapsed <details> cards (invisible modal + body scroll-lock);
// here they render at the top level and the Modal itself portals to <body>.
// Each modal blocks dismissal only while its mutation is genuinely pending,
// with the usePendingEscapeHatch unlock (error OR 30s threshold).
//
// Form bodies are ported from the retired sections.tsx / EventStreamCard
// verbatim — same fields, same validation, same permission semantics.

import { useState } from "react";
import type { ReactNode } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/hooks/useAuth";
import {
  useActivateDACA,
  useAppendWorkoutEvent,
  useCreateEnforcerEngagement,
  useExecuteForbearance,
  useInitiateLegalAction,
  useProposeForbearance,
  useProposeRemediationPlan,
  useRecordWorkoutCost,
  useRecordWorkoutRecovery,
  useSetCaseResolution,
  useSuggestedEnforcers,
} from "@/hooks/portfolioAdmin/usePr4b";
import type {
  LegalActionType,
  WorkoutCostCategory,
  WorkoutEventCategory,
  WorkoutRecoverySource,
} from "@/api/portfolioAdmin/pr4bTypes";
import type { CaseAction } from "./caseStages";
import { usePendingEscapeHatch } from "./usePendingEscapeHatch";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

export type WorkspaceAction =
  | { kind: CaseAction }
  | { kind: "execute-forbearance"; agreementId: string };

interface CaseActionModalsProps {
  caseId: string;
  loanId: string;
  currentRemediationLevel: string | null;
  action: WorkspaceAction | null;
  onClose: () => void;
}

// ----- shared shell ---------------------------------------------------------

interface ActionModalShellProps {
  title: string;
  description?: string;
  submitLabel: string;
  pendingLabel: string;
  isPending: boolean;
  error: string | null;
  destructive?: boolean;
  submitDisabled: boolean;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
}

function ActionModalShell({
  title,
  description,
  submitLabel,
  pendingLabel,
  isPending,
  error,
  destructive,
  submitDisabled,
  onSubmit,
  onClose,
  children,
  testId,
}: ActionModalShellProps) {
  const { canDismiss, pendingNotice } = usePendingEscapeHatch(isPending, error !== null);
  const close = () => {
    if (canDismiss) onClose();
  };
  return (
    <Modal
      open
      onClose={close}
      dismissOnBackdropClick={false}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="tertiary" size="sm" onClick={close} disabled={!canDismiss}>
            Cancel
          </Button>
          <Button
            variant={destructive ? "destructive" : "primary"}
            size="sm"
            onClick={onSubmit}
            disabled={isPending || submitDisabled}
            data-testid={testId ?? "case-action-submit"}
          >
            {isPending ? pendingLabel : submitLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-2">
        {children}
        {error && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}
        {pendingNotice && (
          <p className="text-caption text-gray-mid m-0" role="status">
            {pendingNotice}
          </p>
        )}
      </div>
    </Modal>
  );
}

const inputClass =
  "h-10 px-space-2 border border-border-light rounded-md text-small";
const textareaClass =
  "w-full border border-border-light rounded-md p-space-2 text-small";

// ----- event / note ---------------------------------------------------------

const EVENT_CATEGORIES: { value: WorkoutEventCategory; label: string }[] = [
  { value: "monetary-default", label: "Monetary" },
  { value: "covenant-default", label: "Covenant" },
  { value: "technical-default", label: "Technical" },
  { value: "workout-action", label: "Workout action" },
  { value: "borrower-action", label: "Borrower action" },
  { value: "legal-event", label: "Legal" },
  { value: "document-event", label: "Document" },
  { value: "financial-event", label: "Financial" },
  { value: "state-transition", label: "State transition" },
  { value: "note", label: "Note" },
];

function AddEventModal({
  caseId,
  loanId,
  mode,
  onClose,
}: {
  caseId: string;
  loanId: string;
  mode: "event" | "note";
  onClose: () => void;
}) {
  const append = useAppendWorkoutEvent(caseId);
  const tz = useUserTimeZone();
  const [category, setCategory] = useState<WorkoutEventCategory>(
    mode === "note" ? "note" : "workout-action",
  );
  const [subtype, setSubtype] = useState("");
  const [description, setDescription] = useState("");
  const [dollarImpact, setDollarImpact] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalid =
    description.trim().length < 3 || (mode === "event" && !subtype.trim());

  const handleSubmit = async () => {
    setError(null);
    try {
      // One clock reading, two shapes: `eventDate` is the wire instant (UTC, as
      // every DateTimeOffset is), `effectiveDate` is the calendar day that
      // instant falls on in the VIEWER'S zone. Deriving both from one reading is
      // what stops them disagreeing across a midnight.
      //
      // `stamp.toISOString()` is deliberately NOT wrapped in `new Date(...)`:
      // `calendarDateWrites` matches `new Date(x).toISOString()` because that is
      // how a PICKED CALENDAR DAY ends up stored at UTC midnight, and it cannot
      // tell a millisecond count from a day string. This is an instant, so it
      // must not wear that shape.
      const stamp = new Date();
      await append.mutateAsync({
        loanId,
        eventCategory: category,
        eventSubtype: subtype || (mode === "note" ? "free-text" : "general"),
        eventDate: stamp.toISOString(),
        effectiveDate: isoDateInTz(stamp, tz),
        description,
        dollarImpact: dollarImpact ? Number(dollarImpact) : null,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Append failed");
    }
  };

  return (
    <ActionModalShell
      title={mode === "note" ? "Add note to case" : "Record typed event"}
      description={
        mode === "note"
          ? "Free-text note appended to the immutable event log."
          : "Categorized event appended to the immutable event log."
      }
      submitLabel="Append"
      pendingLabel="Saving…"
      isPending={append.isPending}
      error={error}
      submitDisabled={invalid}
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="append-event-submit"
    >
      {mode === "event" && (
        <>
          <label className="text-small font-bold">Category</label>
          <select
            className={inputClass}
            value={category}
            onChange={(e) => setCategory(e.target.value as WorkoutEventCategory)}
          >
            {EVENT_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <label className="text-small font-bold">Subtype</label>
          <input
            type="text"
            className={inputClass}
            value={subtype}
            onChange={(e) => setSubtype(e.target.value)}
            placeholder="e.g., nsf-letter-sent"
          />
          <label className="text-small font-bold">Dollar impact (optional)</label>
          <input
            type="number"
            step="0.01"
            className={inputClass}
            value={dollarImpact}
            onChange={(e) => setDollarImpact(e.target.value)}
          />
        </>
      )}
      <label className="text-small font-bold">Description</label>
      <textarea
        rows={4}
        className={textareaClass}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
    </ActionModalShell>
  );
}

// ----- remediation plan -------------------------------------------------------

const PLAN_LEVELS: Array<"A" | "B" | "C" | "D"> = ["A", "B", "C", "D"];

function ProposePlanModal({
  caseId,
  currentLevel,
  onClose,
}: {
  caseId: string;
  currentLevel: string | null;
  onClose: () => void;
}) {
  const auth = useAuth();
  const isE1 = auth.hasRoleCode("E1") || auth.hasRoleCode("ADMIN");
  const propose = useProposeRemediationPlan(caseId);
  const tz = useUserTimeZone();
  const [level, setLevel] = useState<"A" | "B" | "C" | "D">("A");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [target, setTarget] = useState(() =>
    isoDateInTz(new Date(Date.now() + 60 * 86400000), tz),
  );
  const [error, setError] = useState<string | null>(null);

  // Mirrors DefaultCaseService.AdvanceRemediationLevelAsync: from a level,
  // only the NEXT level is skip-free; with no level yet, only "A" is.
  // (The old card's `currentLevel && …` guard let any level through on a
  // fresh case — the null branch was the hole.)
  const currentIdx = currentLevel ? PLAN_LEVELS.indexOf(currentLevel as "A") : -1;
  const isSkipping = PLAN_LEVELS.indexOf(level) > currentIdx + 1;

  const handleSubmit = async () => {
    setError(null);
    if (isSkipping && !isE1) {
      setError("Skipping levels requires E1 override.");
      return;
    }
    try {
      await propose.mutateAsync({
        level,
        title,
        description,
        startDate: isoDateInTz(new Date(), tz),
        targetCompletionDate: target,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Propose failed");
    }
  };

  return (
    <ActionModalShell
      title="Propose remediation plan"
      description="Plans cannot skip levels (A → B → C → D) without E1 override."
      submitLabel="Propose"
      pendingLabel="Saving…"
      isPending={propose.isPending}
      error={error}
      submitDisabled={title.trim().length < 3 || description.trim().length < 10}
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="remediation-propose-submit"
    >
      <label className="text-small font-bold">Level</label>
      <select
        className={inputClass}
        value={level}
        onChange={(e) => setLevel(e.target.value as "A" | "B" | "C" | "D")}
      >
        {PLAN_LEVELS.map((l) => (
          <option key={l} value={l}>
            Level {l}
          </option>
        ))}
      </select>
      <label className="text-small font-bold">Title</label>
      <input
        type="text"
        className={inputClass}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <label className="text-small font-bold">Description</label>
      <textarea
        rows={3}
        className={textareaClass}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <label className="text-small font-bold">Target completion date</label>
      <input
        type="date"
        className={inputClass}
        value={target}
        onChange={(e) => setTarget(e.target.value)}
      />
    </ActionModalShell>
  );
}

// ----- forbearance ------------------------------------------------------------

function ProposeForbearanceModal({
  caseId,
  loanId,
  onClose,
}: {
  caseId: string;
  loanId: string;
  onClose: () => void;
}) {
  const propose = useProposeForbearance(caseId);
  const tz = useUserTimeZone();
  const [start, setStart] = useState(() => isoDateInTz(new Date(), tz));
  const [end, setEnd] = useState(() =>
    isoDateInTz(new Date(Date.now() + 90 * 86400000), tz),
  );
  const [type, setType] = useState("payment-deferral");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      await propose.mutateAsync({
        loanId,
        effectiveStartDate: start,
        effectiveEndDate: end,
        forbearanceType: type,
        forbearanceAmount: amount ? Number(amount) : null,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Propose failed");
    }
  };

  return (
    <ActionModalShell
      title="Propose forbearance"
      description="Captures the terms. Borrower signature is captured at execution."
      submitLabel="Propose"
      pendingLabel="Saving…"
      isPending={propose.isPending}
      error={error}
      submitDisabled={false}
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="forbearance-propose-submit"
    >
      <label className="text-small font-bold">Type</label>
      <select
        className={inputClass}
        value={type}
        onChange={(e) => setType(e.target.value)}
      >
        <option value="payment-deferral">Payment deferral</option>
        <option value="interest-only">Interest-only period</option>
        <option value="rate-reduction">Rate reduction</option>
        <option value="term-extension">Term extension</option>
      </select>
      <label className="text-small font-bold">Start date</label>
      <input
        type="date"
        className={inputClass}
        value={start}
        onChange={(e) => setStart(e.target.value)}
      />
      <label className="text-small font-bold">End date</label>
      <input
        type="date"
        className={inputClass}
        value={end}
        onChange={(e) => setEnd(e.target.value)}
      />
      <label className="text-small font-bold">Forbearance amount (optional)</label>
      <input
        type="number"
        step="0.01"
        className={inputClass}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
    </ActionModalShell>
  );
}

function ExecuteForbearanceModal({
  caseId,
  loanId,
  agreementId,
  onClose,
}: {
  caseId: string;
  loanId: string;
  agreementId: string;
  onClose: () => void;
}) {
  const execute = useExecuteForbearance(caseId, loanId);
  const tz = useUserTimeZone();
  const [signer, setSigner] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      await execute.mutateAsync({
        id: agreementId,
        body: {
          borrowerSignedDate: isoDateInTz(new Date(), tz),
          borrowerSignedBy: signer,
        },
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Execute failed");
    }
  };

  return (
    <ActionModalShell
      title="Execute forbearance"
      description="Records the borrower signature and activates the agreement."
      submitLabel="Execute"
      pendingLabel="Saving…"
      isPending={execute.isPending}
      error={error}
      submitDisabled={signer.trim().length < 2}
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="forbearance-execute-submit"
    >
      <label className="text-small font-bold">Borrower signed by</label>
      <input
        type="text"
        className={`${inputClass} w-full`}
        value={signer}
        onChange={(e) => setSigner(e.target.value)}
        placeholder="Name of signing party"
      />
    </ActionModalShell>
  );
}

// ----- DACA -------------------------------------------------------------------

function ActivateDacaModal({
  caseId,
  loanId,
  onClose,
}: {
  caseId: string;
  loanId: string;
  onClose: () => void;
}) {
  const activate = useActivateDACA(caseId);
  const [bankName, setBankName] = useState("");
  const [acct, setAcct] = useState("");
  const [monthly, setMonthly] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      await activate.mutateAsync({
        loanId,
        bankName,
        encryptedAccountNumber: acct,
        monthlyCollectionAmount: Number(monthly),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Activate failed");
    }
  };

  return (
    <ActionModalShell
      title="Activate DACA"
      description="Deposit Account Control Agreement diverts borrower receivables to a Phoenix-controlled bank. E2 approval required."
      submitLabel="Activate DACA"
      pendingLabel="Saving…"
      isPending={activate.isPending}
      error={error}
      destructive
      submitDisabled={bankName.length < 2 || acct.length < 4 || !monthly}
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="daca-activate-submit"
    >
      <label className="text-small font-bold">Bank name</label>
      <input
        type="text"
        className={inputClass}
        value={bankName}
        onChange={(e) => setBankName(e.target.value)}
      />
      <label className="text-small font-bold">Encrypted account number</label>
      <input
        type="password"
        className={inputClass}
        value={acct}
        onChange={(e) => setAcct(e.target.value)}
        autoComplete="off"
      />
      <label className="text-small font-bold">Monthly collection amount</label>
      <input
        type="number"
        step="0.01"
        className={inputClass}
        value={monthly}
        onChange={(e) => setMonthly(e.target.value)}
      />
    </ActionModalShell>
  );
}

// ----- legal action -----------------------------------------------------------

const LEGAL_ACTION_TYPES: { value: LegalActionType; label: string }[] = [
  { value: "demand-letter", label: "Demand letter" },
  { value: "UCC-enforcement", label: "UCC enforcement" },
  { value: "guaranty-enforcement", label: "Guaranty enforcement" },
  { value: "foreclosure", label: "Foreclosure" },
  { value: "bankruptcy-motion", label: "Bankruptcy motion" },
  { value: "litigation", label: "Litigation" },
  { value: "replevin", label: "Replevin" },
  { value: "receivership-petition", label: "Receivership petition" },
];

function InitiateLegalModal({
  caseId,
  loanId,
  onClose,
}: {
  caseId: string;
  loanId: string;
  onClose: () => void;
}) {
  const { data: suggested } = useSuggestedEnforcers(loanId);
  const initiate = useInitiateLegalAction(caseId);
  const [actionType, setActionType] = useState<LegalActionType>("demand-letter");
  const [enforcerId, setEnforcerId] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [caseNumber, setCaseNumber] = useState("");
  const [error, setError] = useState<string | null>(null);

  const enforcerList = suggested ?? [];

  const handleSubmit = async () => {
    setError(null);
    try {
      await initiate.mutateAsync({
        actionType,
        localEnforcerId: enforcerId,
        courtJurisdiction: jurisdiction || null,
        caseNumber: caseNumber || null,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Initiate failed");
    }
  };

  return (
    <ActionModalShell
      title="Initiate legal action"
      description="Selects a Local Enforcer from the registry — free-text law-firm names are not permitted."
      submitLabel="Initiate"
      pendingLabel="Saving…"
      isPending={initiate.isPending}
      error={error}
      submitDisabled={!enforcerId}
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="legal-action-submit"
    >
      <label className="text-small font-bold">Action type</label>
      <select
        className={inputClass}
        value={actionType}
        onChange={(e) => setActionType(e.target.value as LegalActionType)}
      >
        {LEGAL_ACTION_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
      <label className="text-small font-bold">Local Enforcer</label>
      <select
        className={inputClass}
        value={enforcerId}
        onChange={(e) => setEnforcerId(e.target.value)}
        data-testid="legal-action-enforcer-select"
      >
        <option value="">Select enforcer…</option>
        {enforcerList.map((en) => (
          <option key={en.id} value={en.id}>
            {en.legalEntityName} ({en.enforcerType})
          </option>
        ))}
      </select>
      <label className="text-small font-bold">Court jurisdiction</label>
      <input
        type="text"
        className={inputClass}
        value={jurisdiction}
        onChange={(e) => setJurisdiction(e.target.value)}
      />
      <label className="text-small font-bold">Case number</label>
      <input
        type="text"
        className={inputClass}
        value={caseNumber}
        onChange={(e) => setCaseNumber(e.target.value)}
      />
    </ActionModalShell>
  );
}

// ----- enforcer engagement ------------------------------------------------------

function EngageEnforcerModal({
  caseId,
  loanId,
  onClose,
}: {
  caseId: string;
  loanId: string;
  onClose: () => void;
}) {
  const { data: suggested } = useSuggestedEnforcers(loanId);
  const createEng = useCreateEnforcerEngagement(caseId);
  const tz = useUserTimeZone();
  const [enforcerId, setEnforcerId] = useState("");
  const [engagementType, setEngagementType] = useState("repossession");
  const [feeStructure, setFeeStructure] = useState("hourly");
  const [feeAmount, setFeeAmount] = useState("");
  const [error, setError] = useState<string | null>(null);

  const enforcerList = suggested ?? [];

  const handleSubmit = async () => {
    setError(null);
    try {
      const today = isoDateInTz(new Date(), tz);
      const ninetyOut = isoDateInTz(new Date(Date.now() + 90 * 86400000), tz);
      await createEng.mutateAsync({
        enforcerId,
        body: {
          loanId,
          defaultCaseId: caseId,
          engagementType,
          engagementStartDate: today,
          expectedEndDate: ninetyOut,
          feeStructure,
          feeAmount: Number(feeAmount),
          reportingCadence: "monthly",
        },
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Engage failed");
    }
  };

  return (
    <ActionModalShell
      title="Engage enforcer"
      description="Top suggestions are ranked by performance, in-jurisdiction, and active status."
      submitLabel="Engage"
      pendingLabel="Saving…"
      isPending={createEng.isPending}
      error={error}
      submitDisabled={!enforcerId || !feeAmount}
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="engage-enforcer-submit"
    >
      <label className="text-small font-bold">Suggested enforcers</label>
      <ul
        className="m-0 p-0 list-none border border-border-light rounded-md max-h-48 overflow-y-auto"
        data-testid="suggested-enforcers-list"
      >
        {enforcerList.slice(0, 5).map((en) => (
          <li key={en.id} className="border-b border-border-light last:border-0">
            <label className="flex items-center gap-space-2 px-space-2 py-space-2 cursor-pointer hover:bg-white-warm">
              <input
                type="radio"
                name="enforcer-select"
                value={en.id}
                checked={enforcerId === en.id}
                onChange={() => setEnforcerId(en.id)}
              />
              <span className="text-small">
                <strong>{en.legalEntityName}</strong>{" "}
                <span className="text-gray-mid">
                  — {en.enforcerType} • {en.status}
                </span>
              </span>
            </label>
          </li>
        ))}
        {enforcerList.length === 0 && (
          <li className="p-space-2 text-caption text-gray-mid italic">
            No suggestions returned. Use the Local Enforcer registry to search
            the full directory.
          </li>
        )}
      </ul>
      <label className="text-small font-bold">Engagement type</label>
      <select
        className={inputClass}
        value={engagementType}
        onChange={(e) => setEngagementType(e.target.value)}
      >
        <option value="repossession">Repossession</option>
        <option value="replevin">Replevin</option>
        <option value="property-securement">Property securement</option>
        <option value="foreclosure-counsel">Foreclosure counsel</option>
        <option value="litigation-counsel">Litigation counsel</option>
        <option value="auction-services">Auction services</option>
        <option value="site-monitoring">Site monitoring</option>
        <option value="bankruptcy-counsel">Bankruptcy counsel</option>
      </select>
      <label className="text-small font-bold">Fee structure</label>
      <select
        className={inputClass}
        value={feeStructure}
        onChange={(e) => setFeeStructure(e.target.value)}
      >
        <option value="hourly">Hourly</option>
        <option value="flat-fee">Flat fee</option>
        <option value="contingency">Contingency</option>
        <option value="retainer-plus-hourly">Retainer + hourly</option>
      </select>
      <label className="text-small font-bold">Fee amount</label>
      <input
        type="number"
        step="0.01"
        className={inputClass}
        value={feeAmount}
        onChange={(e) => setFeeAmount(e.target.value)}
      />
    </ActionModalShell>
  );
}

// ----- costs / recoveries -------------------------------------------------------

const COST_CATEGORIES: { value: WorkoutCostCategory; label: string }[] = [
  { value: "legal-fees", label: "Legal fees" },
  { value: "enforcer-fees", label: "Enforcer fees" },
  { value: "appraisal", label: "Appraisal" },
  { value: "site-securement", label: "Site securement" },
  { value: "transport", label: "Transport" },
  { value: "storage", label: "Storage" },
  { value: "auction-fees", label: "Auction fees" },
  { value: "insurance-force-placed", label: "Insurance (force-placed)" },
  { value: "tax-payment", label: "Tax payment" },
  { value: "filing-fees", label: "Filing fees" },
  { value: "other", label: "Other" },
];

function RecordCostModal({
  caseId,
  loanId,
  onClose,
}: {
  caseId: string;
  loanId: string;
  onClose: () => void;
}) {
  const record = useRecordWorkoutCost(caseId);
  const tz = useUserTimeZone();
  const [category, setCategory] = useState<WorkoutCostCategory>("legal-fees");
  const [amount, setAmount] = useState("");
  const [recoverable, setRecoverable] = useState(true);
  const [docsCertified, setDocsCertified] = useState(false);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Recoverable costs route into B3's Fees.Review queue and bill the borrower,
  // so B4 must certify the supporting documentation is on file first. A
  // non-recoverable cost is case-only tracking — no certification gate.
  const certRequired = recoverable && !docsCertified;

  const handleSubmit = async () => {
    setError(null);
    try {
      await record.mutateAsync({
        loanId,
        incurredDate: isoDateInTz(new Date(), tz),
        costCategory: category,
        amount: Number(amount),
        recoverableFromBorrower: recoverable,
        docsCertified: recoverable ? docsCertified : false,
        notes: notes || null,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Record failed");
    }
  };

  return (
    <ActionModalShell
      title="Record workout cost"
      description="Recoverable costs route to B3's Fee review queue and bill the borrower. Non-recoverable costs are tracked on the case only."
      submitLabel="Record"
      pendingLabel="Saving…"
      isPending={record.isPending}
      error={error}
      submitDisabled={!amount || certRequired}
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="cost-record-submit"
    >
      <label className="text-small font-bold">Category</label>
      <select
        className={inputClass}
        value={category}
        onChange={(e) => setCategory(e.target.value as WorkoutCostCategory)}
      >
        {COST_CATEGORIES.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>
      <label className="text-small font-bold">Amount</label>
      <input
        type="number"
        step="0.01"
        className={inputClass}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <label className="flex items-center gap-space-2 text-small mt-space-1">
        <input
          type="checkbox"
          checked={recoverable}
          onChange={(e) => setRecoverable(e.target.checked)}
        />
        Recoverable from borrower
      </label>
      {recoverable && (
        <div className="mt-space-1 rounded-md border border-gray-mid/40 bg-white-warm p-space-2">
          <label className="flex items-start gap-space-2 text-small">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={docsCertified}
              onChange={(e) => setDocsCertified(e.target.checked)}
              data-testid="cost-docs-certify"
            />
            <span>
              I certify the supporting documentation for this cost has been
              uploaded to the loan{" "}
              <a
                href={`/portfolio-admin/loans/${loanId}?tab=documents`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-gold underline"
              >
                document repository
              </a>
              . Required before B3 review.
            </span>
          </label>
        </div>
      )}
      <label className="text-small font-bold">Notes</label>
      <textarea
        rows={3}
        className={textareaClass}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
    </ActionModalShell>
  );
}

const RECOVERY_SOURCES: { value: WorkoutRecoverySource; label: string }[] = [
  { value: "borrower-post-default", label: "Borrower (post-default)" },
  { value: "guarantor", label: "Guarantor" },
  { value: "collateral-sale", label: "Collateral sale" },
  { value: "insurance", label: "Insurance" },
  { value: "DACA-sweep", label: "DACA sweep" },
  { value: "judgment", label: "Judgment" },
  { value: "settlement", label: "Settlement" },
  { value: "deficiency", label: "Deficiency" },
  { value: "other", label: "Other" },
];

function RecordRecoveryModal({
  caseId,
  loanId,
  onClose,
}: {
  caseId: string;
  loanId: string;
  onClose: () => void;
}) {
  const record = useRecordWorkoutRecovery(caseId);
  const tz = useUserTimeZone();
  const [source, setSource] = useState<WorkoutRecoverySource>("borrower-post-default");
  const [gross, setGross] = useState("");
  const [costs, setCosts] = useState("0");
  const [depositAccount, setDepositAccount] = useState("");
  const [waterfall, setWaterfall] = useState("WF1");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      await record.mutateAsync({
        loanId,
        recoveryDate: isoDateInTz(new Date(), tz),
        recoverySource: source,
        grossAmount: Number(gross),
        recoveryCosts: Number(costs || "0"),
        depositAccount,
        appliedToWaterfall: waterfall,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Record failed");
    }
  };

  return (
    <ActionModalShell
      title="Record recovery"
      description="E1 approval required. Gross − costs = net applied to waterfall."
      submitLabel="Record"
      pendingLabel="Saving…"
      isPending={record.isPending}
      error={error}
      submitDisabled={!gross || !depositAccount}
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="recovery-record-submit"
    >
      <label className="text-small font-bold">Source</label>
      <select
        className={inputClass}
        value={source}
        onChange={(e) => setSource(e.target.value as WorkoutRecoverySource)}
      >
        {RECOVERY_SOURCES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
      <label className="text-small font-bold">Gross amount</label>
      <input
        type="number"
        step="0.01"
        className={inputClass}
        value={gross}
        onChange={(e) => setGross(e.target.value)}
      />
      <label className="text-small font-bold">Recovery costs</label>
      <input
        type="number"
        step="0.01"
        className={inputClass}
        value={costs}
        onChange={(e) => setCosts(e.target.value)}
      />
      <label className="text-small font-bold">Deposit account</label>
      <input
        type="text"
        className={inputClass}
        value={depositAccount}
        onChange={(e) => setDepositAccount(e.target.value)}
      />
      <label className="text-small font-bold">Applied to waterfall</label>
      <select
        className={inputClass}
        value={waterfall}
        onChange={(e) => setWaterfall(e.target.value)}
      >
        <option value="WF1">WF1 — Retained-share</option>
        <option value="WF2">WF2 — Contributed-share</option>
      </select>
    </ActionModalShell>
  );
}

// ----- closure ----------------------------------------------------------------

const RESOLUTION_TYPES = [
  "cured",
  "restructured",
  "refinanced",
  "sold",
  "foreclosed",
  "written-off",
];

function CloseCaseModal({
  caseId,
  loanId,
  onClose,
}: {
  caseId: string;
  loanId: string;
  onClose: () => void;
}) {
  const setResolution = useSetCaseResolution(caseId, loanId);
  const [resolutionType, setResolutionType] = useState("written-off");
  const [totalLoss, setTotalLoss] = useState("");
  const [recovery, setRecovery] = useState("");
  const [narrative, setNarrative] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      await setResolution.mutateAsync({
        resolutionType,
        totalLossAmount: totalLoss ? Number(totalLoss) : null,
        recoveryAmount: recovery ? Number(recovery) : null,
        closureNarrative: narrative.trim(),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Close failed");
    }
  };

  return (
    <ActionModalShell
      title="Close default case (E1)"
      description="Records the resolution. Use Transition state → closed final to lock the case once the resolution is recorded."
      submitLabel="Record resolution"
      pendingLabel="Closing…"
      isPending={setResolution.isPending}
      error={error}
      destructive
      submitDisabled={narrative.trim().length < 50}
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="close-case-confirm"
    >
      <label className="text-small font-bold">Resolution type</label>
      <select
        className={inputClass}
        value={resolutionType}
        onChange={(e) => setResolutionType(e.target.value)}
      >
        {RESOLUTION_TYPES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <label className="text-small font-bold">Total loss</label>
      <input
        type="number"
        step="0.01"
        className={inputClass}
        value={totalLoss}
        onChange={(e) => setTotalLoss(e.target.value)}
      />
      <label className="text-small font-bold">Total recovery</label>
      <input
        type="number"
        step="0.01"
        className={inputClass}
        value={recovery}
        onChange={(e) => setRecovery(e.target.value)}
      />
      <label className="text-small font-bold">
        Closure narrative (≥ 50 characters)
      </label>
      <textarea
        rows={4}
        className={textareaClass}
        value={narrative}
        onChange={(e) => setNarrative(e.target.value)}
      />
    </ActionModalShell>
  );
}

// ----- dispatcher --------------------------------------------------------------

export function CaseActionModals({
  caseId,
  loanId,
  currentRemediationLevel,
  action,
  onClose,
}: CaseActionModalsProps) {
  if (!action) return null;
  switch (action.kind) {
    case "add-event":
      return <AddEventModal caseId={caseId} loanId={loanId} mode="event" onClose={onClose} />;
    case "add-note":
      return <AddEventModal caseId={caseId} loanId={loanId} mode="note" onClose={onClose} />;
    case "propose-plan":
      return (
        <ProposePlanModal
          caseId={caseId}
          currentLevel={currentRemediationLevel}
          onClose={onClose}
        />
      );
    case "propose-forbearance":
      return <ProposeForbearanceModal caseId={caseId} loanId={loanId} onClose={onClose} />;
    case "execute-forbearance":
      return (
        <ExecuteForbearanceModal
          caseId={caseId}
          loanId={loanId}
          agreementId={action.agreementId}
          onClose={onClose}
        />
      );
    case "activate-daca":
      return <ActivateDacaModal caseId={caseId} loanId={loanId} onClose={onClose} />;
    case "initiate-legal":
      return <InitiateLegalModal caseId={caseId} loanId={loanId} onClose={onClose} />;
    case "engage-enforcer":
      return <EngageEnforcerModal caseId={caseId} loanId={loanId} onClose={onClose} />;
    case "record-cost":
      return <RecordCostModal caseId={caseId} loanId={loanId} onClose={onClose} />;
    case "record-recovery":
      return <RecordRecoveryModal caseId={caseId} loanId={loanId} onClose={onClose} />;
    case "close-case":
      return <CloseCaseModal caseId={caseId} loanId={loanId} onClose={onClose} />;
    default:
      return null;
  }
}
